import { useEffect, useRef, useState } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import clsx from 'clsx';
import { IProjectItemOverview, IProjectService } from 'common/types/projects';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

gsap.registerPlugin(ScrollTrigger);

export const WorkCaseStudiesCarousel: React.FC<{ services?: string[] }> = ({ services }) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [imagesError, setImagesError] = useState<string[]>([]);

  const { t } = useTranslation('projectItemsOverview');
  const projectsData = t('projectItemsOverview:projectItems', {
    returnObjects: true,
    defaultValue: [],
  }) as unknown;
  let projects = Array.isArray(projectsData) ? (projectsData as IProjectItemOverview[]) : [];
  projects = projects.filter(item => item.active);
  if (services?.length) {
    projects = projects.filter(item =>
      item.services.some(service => services.includes(service.slug)),
    );
  }

  const handleImageError =
    (src: string) => (_event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setImagesError(prev => [...prev, src]);
    };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const syncBreakpoint = () => {
      setIsLargeScreen(mediaQuery.matches);
    };
    syncBreakpoint();
    mediaQuery.addEventListener('change', syncBreakpoint);
    return () => {
      mediaQuery.removeEventListener('change', syncBreakpoint);
    };
  }, []);

  useEffect(() => {
    if (!sectionRef.current || !projects.length || !isLargeScreen) return;

    const ctx = gsap.context(() => {
      const projectCards = gsap.utils.toArray<HTMLElement>('[data-projects-card]');

      projectCards.forEach((project, index) => {
        gsap.set(project, {
          transformOrigin: 'center top',
          zIndex: index + 1,
        });
      });

      projectCards.forEach((project, index) => {
        const currentPanel = document.querySelector(`#project-item-panel-${index + 1}`);
        gsap.fromTo(
          project,
          { scale: 1 },
          {
            scale: 0.95,
            ease: 'none',
            scrollTrigger: {
              trigger: currentPanel,
              start: 'top 50%',
              end: 'top 0%',
              scrub: true,
            },
          },
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [projects, isLargeScreen]);

  const renderArticle = (
    {
      color,
      imageHomepage,
      intro,
      services: servicesProjectItem,
      seo,
      slug,
      title,
    }: IProjectItemOverview,
    index: number,
  ) => {
    const top = index + 1 === projects.length ? 76 : 85;
    return (
      <article
        key={slug}
        data-projects-card
        id={`project-item-panel-${index + 1}`}
        className={clsx(
          'group card mb-2',
          'origin-[50% 50%] w-full rounded-[0] lg:sticky rounded-[30px]',
          `card-${index}`,
          {
            'bg-gradient border-[10px] border-[#152243] text-white': color === 'blue',
            'bg-gradient border-[10px] border-[#136534] text-white': color === 'officium',
            'bg-gradient border-[10px] border-[#3f1d56] text-white': color === 'purple',
            'bg-gradient border-[10px] border-[#a00000] text-white': color === 'red',
            'bg-gradient border-[10px] border-[#935513] text-white': color === 'yellow',
          },
        )}
        style={{ zIndex: index + 1, top: isLargeScreen ? top : undefined }}>
        <div className="flex flex-wrap gap-1.5 px-4 pt-6 md:pt-10 lg:px-12 lg:pt-12">
          {servicesProjectItem.map((service: IProjectService, serviceIndex: number) => {
            if (service.href) {
              return (
                <Link
                  className="z-50 cursor-pointer bg-white/10 p-3 py-2 text-[16px] hover:border-primary hover:text-primary"
                  href={service.href}
                  onClick={e => {
                    e.stopPropagation();
                  }}
                  key={`${service.slug}-${serviceIndex}`}>
                  {service.label}
                </Link>
              );
            }
            return (
              <div
                className="cursor-pointer bg-white/10 p-3 py-2 text-[16px] hover:border-primary hover:text-primary"
                key={`${service.slug}-${serviceIndex}`}>
                {service.label}
              </div>
            );
          })}
        </div>
        <div className="flex h-auto flex-col justify-between overflow-hidden pb-0 duration-300 lg:mb-0 lg:pl-12 lg:pr-0 xl:flex-row">
          <div className="relative flex w-full flex-col justify-between px-4 lg:pb-12 xl:w-2/5">
            <div className="pb-10 lg:pb-20">
              <h2
                className="text-heading-work-item mt-4 md:mr-3 lg:mt-6"
                dangerouslySetInnerHTML={{ __html: title }}
              />
              <div className="mb-2 mt-6 opacity-90" dangerouslySetInnerHTML={{ __html: intro }} />
            </div>
            <div className="mt-5 flex items-center">
              {t('common:readMore')}{' '}
              <BsArrowRight
                className="ml-3 mt-1 transition-all duration-300 group-hover:ml-6"
                size={20}
              />
            </div>
          </div>

          <div className="justify-en flex w-full items-end pl-4 xl:w-3/5 pr-2 pb-2 rounded-br-[30px] overflow-hidden">
            {!imagesError.includes(imageHomepage.src) && (
              <Image
                alt={imageHomepage.alt}
                className="h-full w-full max-w-[100%] object-contain object-right-bottom rounded-br-[24px]"
                src={imageHomepage.src}
                onError={handleImageError(imageHomepage.src)}
                height={imageHomepage.height}
                width={imageHomepage.width}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
              />
            )}
          </div>
        </div>
        <Link
          aria-label={`${t('common:readMore')} - ${seo.title}`}
          href={slug}
          className="cursor-open absolute bottom-0 left-0 right-0 top-0 "
        />
      </article>
    );
  };

  return (
    <>
      <section id="cards" className="bg-gradient relative py-1" ref={sectionRef}>
        <div className="mx-auto w-full max-w-[95vw] 3xl:container">
          {projects.map(renderArticle)}
        </div>
      </section>
    </>
  );
};
